const BRAND = 'HOLIC'
const DESCRIPTION_MIN = 80
const DESCRIPTION_MAX = 120
const OWNER_FALLBACK = 'HOLICユーザー'

const DEFAULT_TITLE = 'HOLIC [ホリック] • 新トレカ売買サイト'
const DEFAULT_DESCRIPTION = 'あなたのポケモンカードゲームのための一つのショップ！あなたのカードを他のプレイヤーに販売するためにあなたのショップを設定します！'

// Collector-facing JA labels. Keys match printed English rarity names.
const RARITY_JA: Record<string, string> = {
  'Amazing Rare': 'A',
  'Common (Mirror)': 'C (ミラー)',
  Common: 'C',
  'Character Rare': 'CHR',
  'Character Super Rare': 'CSR',
  'Hyper Rare': 'HR',
  'Shiny Rare (K)': 'K',
  Promo: 'P (プロモ)',
  'Rare (Mirror)': 'R (ミラー)',
  'Rare Holo': 'R/キラ★',
  'Ultra Rare': 'RR',
  'Double Rare': 'RR',
  'Triple Rare': 'RRR',
  'Shiny Rare': 'S',
  'Secret Rare': 'SR',
  'Super Shiny Rare': 'SSR',
  'Trainer Rare': 'TR',
  'Uncommon (Mirror)': 'U (ミラー)',
  Uncommon: 'U',
  UR: 'UR',
  'Uncommon (white diamond)': '◇',
  Rare: '★',
  'Rare (two stars)': '★★',
  'Rare (three stars)': '★★★',
  'Rare (white star)': '☆',
  None: '',
  'Holo (no symbol)': 'なし (キラ)',
  'Mirror (no symbol)': 'なし (ミラー)',
}

const CONDITION_JA: Record<number, string> = {
  0: 'ランク不明',
  100: 'ランクX',
  90: 'ランクS',
  80: 'ランクA+',
  70: 'ランクA',
  60: 'ランクA-',
  50: 'ランクB',
  40: 'ランクB',
  30: 'ランクC',
  20: 'ランクC',
  15: 'ランクC',
  10: 'ランクF',
}

type PageMeta = Readonly<{
  title: string
  description: string
  url?: string | null
  type?: 'website' | 'product'
  imageUrl?: string | null
  imageWidth?: number | null
  imageHeight?: number | null
  imageAlt?: string | null
  priceAmount?: string | null
}>

type CardMetaInput = Readonly<{
  id?: number
  card: string
  setName?: string
  edition?: string
  rarity?: string
  illustrator?: string
  cardNumber?: string
  askCount?: number
  minimumPriceJpy?: number
  image?: SeoImage | null
}>

type SetMetaInput = Readonly<{
  id?: number
  name: string
  releaseDate?: string
  cardCount?: number
  seriesName?: string
  image?: SeoImage | null
}>

type ListingMetaInput = Readonly<{
  id?: number
  card: string
  setName?: string
  edition?: string
  condition?: number
  priceJpy?: number
  seller?: string
  illustrator?: string
  cardNumber?: string
  sold?: boolean
  image?: SeoImage | null
}>

type CollectionMetaInput = Readonly<{
  owner?: string
  id: number
  salesRating?: number
  salesLevel?: number
}>

type SeoImage = Readonly<{
  imageUrl?: string | null
  width?: number | null
  height?: number | null
  sprite?: boolean
}>

type SeoNamedTag = Readonly<{
  type?: string
  depth?: number
  nameJa?: string
  nameEn?: string
}>

type SeoTag = SeoNamedTag & Readonly<{
  parent?: SeoNamedTag
  parents?: readonly SeoNamedTag[]
}>

type SeoItem = Readonly<{
  id?: number
  nameJa?: string
  nameEn?: string
  holicListingsCount?: number
  holicMinimumPrice?: number
  cardNumber?: string
  illustrator?: string
  printedRarity?: string
  tags?: readonly SeoTag[]
  images?: readonly SeoImage[]
  itemDatas?: readonly Readonly<{
    body?: Readonly<{
      artist?: string
      number?: string
      rarity?: string
      numbers?: readonly string[]
    }>
  }>[]
}>

type SeoListing = Readonly<{
  platform?: string
  priceJpy?: number
  soldAt?: string | null
}>

type SeoUnit = Readonly<{
  id?: number
  condition?: number
  user?: Readonly<{ displayName?: string }>
  listings?: readonly SeoListing[]
  images?: readonly SeoImage[]
  items?: readonly SeoItem[]
}>

const lengthOf = (value: string) => Array.from(value).length

const clean = (value?: string | null): string | undefined => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

const priceAmountOf = (value?: number | null) => {
  if (value == null || !Number.isFinite(value) || value <= 0) return null
  const rounded = Math.round(value * 100) / 100
  if (Math.abs(rounded - Math.round(rounded)) < 0.001) return String(Math.round(rounded))
  return rounded.toFixed(2)
}

const shareImageOf = (images?: readonly SeoImage[] | null) => images?.find(image => !image.sprite && image.imageUrl)

const withShare = (
  meta: Pick<PageMeta, 'title' | 'description'>,
  image: SeoImage | null | undefined,
  options: { type?: 'website' | 'product', price?: number | null, alt: string }
): PageMeta => {
  const priceAmount = options.type === 'product' ? priceAmountOf(options.price) : null
  const width = image?.width != null ? Number(image.width) : null
  const height = image?.height != null ? Number(image.height) : null

  return {
    ...meta,
    type: priceAmount ? 'product' : 'website',
    priceAmount,
    imageUrl: image?.imageUrl ?? null,
    imageWidth: width && height && width > 0 && height > 0 ? width : null,
    imageHeight: width && height && width > 0 && height > 0 ? height : null,
    imageAlt: image?.imageUrl ? options.alt : null,
  }
}

const escapeHtml = (value: string) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')

const formatYen = (price: number) => `¥${Math.round(price).toLocaleString('ja-JP')}`

const formatReleaseDate = (value?: string): string | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '')
  if (!match) return

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (!month || !day) return

  return `${year}年${month}月${day}日`
}

const rarityLabel = (rarity?: string): string | undefined => {
  const raw = clean(rarity)
  if (!raw) return

  if (Object.prototype.hasOwnProperty.call(RARITY_JA, raw)) {
    const label = RARITY_JA[raw]
    return label || undefined
  }

  if (raw === 'なし') return
  return raw
}

const conditionLabel = (condition?: number): string => {
  if (condition == null) return CONDITION_JA[0]
  return CONDITION_JA[condition] ?? `ランク${condition}`
}

const ownerName = (owner?: string) => clean(owner) ?? OWNER_FALLBACK

const withHonorific = (owner: string) => owner === OWNER_FALLBACK ? owner : `${owner}さん`

const fitDescription = (sentences: readonly string[], fillers: readonly string[]): string => {
  let text = ''

  for (const sentence of sentences) {
    const piece = sentence.trim()
    if (!piece) continue

    if (!text && lengthOf(piece) > DESCRIPTION_MAX) {
      const clipped = Array.from(piece).slice(0, DESCRIPTION_MAX).join('')
      const lastStop = clipped.lastIndexOf('。')
      text = lastStop >= DESCRIPTION_MIN - 1 ? clipped.slice(0, lastStop + 1) : clipped
      break
    }

    if (lengthOf(text + piece) <= DESCRIPTION_MAX) text += piece
  }

  for (const filler of fillers) {
    if (lengthOf(text) >= DESCRIPTION_MIN) break
    if (!text.includes(filler) && lengthOf(text + filler) <= DESCRIPTION_MAX) text += filler
  }

  if (lengthOf(text) < DESCRIPTION_MIN) {
    const extra = '詳細はこのページにまとまっています。'
    if (!text.includes(extra) && lengthOf(text + extra) <= DESCRIPTION_MAX) text += extra
  }

  return text
}

const cardPageMeta = (input: CardMetaInput): PageMeta => {
  const card = clean(input.card) ?? 'カード'
  const setName = clean(input.setName)
  const edition = clean(input.edition)
  const rarity = rarityLabel(input.rarity)
  const illustrator = clean(input.illustrator)
  const cardNumber = clean(input.cardNumber)
  const title = `${[card, setName, edition, rarity].filter(Boolean).join(' ')} の販売・相場 | ${BRAND}`

  const identity = [setName, edition, cardNumber]
  if (!setName && !edition && !cardNumber && input.id != null) identity.push(`No.${input.id}`)

  const where = identity.filter(Boolean).join('、')
  const head = where ? `「${card}」（${where}）の出品価格です。` : `「${card}」の出品価格です。`
  const facts = [
    illustrator ? `イラストレーターは${illustrator}` : '',
    rarity ? `レアリティは${rarity}` : '',
  ].filter(Boolean)
  const factSentence = facts.length > 0 ? `${facts.join('、')}。` : ''
  const asks = `現在の出品は${input.askCount ?? 0}件です。`
  const minimum = input.minimumPriceJpy != null && input.minimumPriceJpy > 0
    ? `最安は${formatYen(input.minimumPriceJpy)}です。`
    : ''

  return withShare({
    title,
    description: fitDescription([head, factSentence, asks, minimum], [
      'セット名やカード番号が違う同名カードとは別のページです。',
      '実物の写真と在庫の有無をこのページで確認できます。',
    ]),
  }, input.image, {
    alt: input.card,
    type: (input.askCount ?? 0) > 0 ? 'product' : 'website',
    price: input.minimumPriceJpy,
  })
}

const setPageMeta = (input: SetMetaInput): PageMeta => {
  const name = clean(input.name) ?? 'セット'
  const release = formatReleaseDate(input.releaseDate)
  const seriesName = clean(input.seriesName)
  const cardCount = input.cardCount != null && input.cardCount > 0 ? input.cardCount : undefined
  const bits = [
    release ? `${release}発売` : '',
    cardCount != null ? `収録${cardCount}種` : '',
  ].filter(Boolean)

  const hasFacts = bits.length > 0 || !!seriesName

  return withShare({
    title: `${name} 収録カード一覧・販売 | ${BRAND}`,
    description: fitDescription([
      `「${name}」の収録カード一覧です。`,
      bits.length > 0 ? `${bits.join('、')}。` : '',
      seriesName ? `シリーズは${seriesName}です。` : '',
      !hasFacts && input.id != null ? `セット番号は${input.id}です。` : '',
    ], [
      '収録カードのシングルを価格で比較できます。',
      '一覧から1枚ずつ選んで在庫と価格を確認できます。',
    ]),
  }, input.image, { alt: name, type: 'website' })
}

const listingPageMeta = (input: ListingMetaInput): PageMeta => {
  const card = clean(input.card) ?? 'カード'
  const seller = ownerName(input.seller)
  const condition = conditionLabel(input.condition)
  const cardNumber = clean(input.cardNumber)
  const setName = clean(input.setName)
  const edition = clean(input.edition)
  const illustrator = clean(input.illustrator)
  const numberBit = cardNumber ? `（${cardNumber}）` : ''
  const sellerBit = input.id != null ? `${seller}（出品No.${input.id}）` : seller
  const setLabel = [setName, edition].filter(Boolean).join('、')

  if (input.sold || input.priceJpy == null) {
    return withShare({
      title: input.sold
        ? `${card} ${condition} 売約済み | ${seller} | ${BRAND}`
        : `${card} ${condition} | ${seller}のコレクション | ${BRAND}`,
      description: fitDescription([
        input.sold
          ? `${card}${numberBit}の${condition}出品は売約済みです。`
          : `${card}${numberBit}の${condition}です。`,
        `出品者は${sellerBit}。`,
        setLabel ? `セットは${setLabel}です。` : '',
        illustrator ? `イラストレーターは${illustrator}。` : '',
        input.sold ? '' : 'いま出品中の価格はありません。',
      ], [
        '同じカードの在庫はカードページで確認できます。',
        '状態のメモと写真をこのページで見られます。',
      ]),
    }, input.image, { alt: card, type: 'website' })
  }

  return withShare({
    title: `${card} ${condition} ${formatYen(input.priceJpy)} | ${seller} | ${BRAND}`,
    description: fitDescription([
      `${card}${numberBit}の${condition}出品です。`,
      `価格は${formatYen(input.priceJpy)}、出品者は${sellerBit}。`,
      illustrator ? `イラストレーターは${illustrator}。` : '',
      setLabel ? `セットは${setLabel}です。` : '',
    ], [
      '状態と写真を確認して購入できます。',
      'この出品だけの価格と状態を掲載しています。',
    ]),
  }, input.image, { alt: card, type: 'product', price: input.priceJpy })
}

const collectionPageMeta = (input: CollectionMetaInput): PageMeta => {
  const owner = ownerName(input.owner)
  const rating = input.salesRating != null
    ? `評価は${input.salesRating}、取引レベルは${input.salesLevel ?? 0}です。`
    : ''

  return withShare({
    title: `${owner}のポケモンカードコレクション | ${BRAND}`,
    description: fitDescription([
      `${withHonorific(owner)}（No.${input.id}）のポケモンカードコレクションです。`,
      rating,
    ], [
      '公開中のカードと出品中の1枚を、状態と価格つきで確認できます。',
      'コレクションの中からカードを1枚ずつ探せます。',
    ]),
  }, null, { alt: owner, type: 'website' })
}

const tagName = (tag?: SeoNamedTag) => clean(tag?.nameJa) ?? clean(tag?.nameEn)

const setAndEdition = (tags?: readonly SeoTag[]) => {
  const categories = (tags ?? []).filter(tag => tag.type === 'Category' && (tag.depth ?? 0) > 0)
  if (categories.length === 0) return {}

  const leaf = categories.reduce((prev, current) => (current.depth ?? 0) > (prev.depth ?? 0) ? current : prev)
  if ((leaf.depth ?? 0) <= 2) return { setName: tagName(leaf) }

  const leafDepth = leaf.depth ?? 0
  const ancestors = [
    ...categories.filter(tag => (tag.depth ?? 0) < leafDepth),
    ...(leaf.parent && (leaf.parent.depth ?? 0) > 0 ? [leaf.parent] : []),
    ...(leaf.parents ?? []).filter(tag => (tag.depth ?? 0) > 0 && (tag.depth ?? 0) < leafDepth),
  ]
  const set = ancestors.reduce<SeoNamedTag | undefined>((best, tag) => {
    if (!best || (tag.depth ?? 0) > (best.depth ?? 0)) return tag
    return best
  }, undefined)
  const setName = tagName(set)

  return setName ? { setName, edition: tagName(leaf) } : { setName: tagName(leaf) }
}

const cardFactsFromItem = (item: SeoItem) => {
  const data = item.itemDatas?.[0]?.body
  const numberFromList = data?.numbers?.map(number => clean(number)).find(Boolean)
  const illustratorTag = item.tags?.find(tag => tag.type === 'Illustrator')
  const rarityTag = item.tags?.find(tag => tag.type === 'Rarity')

  return {
    card: clean(item.nameJa) ?? clean(item.nameEn) ?? 'カード',
    ...setAndEdition(item.tags),
    cardNumber: clean(item.cardNumber) ?? clean(data?.number) ?? numberFromList,
    illustrator: clean(item.illustrator) ?? tagName(illustratorTag) ?? clean(data?.artist),
    rarity: clean(item.printedRarity) ?? clean(data?.rarity) ?? tagName(rarityTag),
  }
}

const cardPageMetaFromItem = (item: SeoItem): PageMeta => {
  return cardPageMeta({
    id: item.id,
    ...cardFactsFromItem(item),
    askCount: item.holicListingsCount ?? 0,
    minimumPriceJpy: item.holicMinimumPrice,
    image: shareImageOf(item.images),
  })
}

const liveListing = (listings: readonly SeoListing[]): SeoListing | undefined => {
  const unsold = listings.filter(listing => !listing.soldAt && listing.priceJpy != null)
  return unsold.find(listing => listing.platform === 'holic') ?? unsold[0]
}

const unitPageMeta = (unit: SeoUnit): PageMeta => {
  const item = unit.items?.[0]
  const facts = cardFactsFromItem(item ?? {})
  const listings = unit.listings ?? []
  const sold = listings.some(listing => !!listing.soldAt)
  const listing = sold ? undefined : liveListing(listings)

  return listingPageMeta({
    id: unit.id,
    card: facts.card,
    setName: facts.setName,
    edition: facts.edition,
    condition: unit.condition,
    priceJpy: listing?.priceJpy,
    seller: unit.user?.displayName,
    illustrator: facts.illustrator,
    cardNumber: facts.cardNumber,
    sold,
    image: shareImageOf(unit.images) ?? shareImageOf(item?.images),
  })
}

const defaultPageMeta = (): PageMeta => ({
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
})

const upsertMeta = (attr: 'property' | 'name', key: string, content: string | null) => {
  const selector = `meta[${attr}="${key}"]`
  const existing = document.head.querySelector<HTMLMetaElement>(selector)
  if (content == null || content === '') {
    existing?.remove()
    return
  }

  const element = existing ?? document.createElement('meta')
  element.setAttribute(attr, key)
  element.setAttribute('content', content)
  if (!existing) document.head.appendChild(element)
}

const absoluteImage = (imageUrl?: string | null) => {
  const value = imageUrl?.trim() ?? ''
  if (!value) return null
  if (/^https?:\/\//i.test(value)) return value
  if (value.startsWith('//')) return `https:${value}`
  if (typeof window === 'undefined' || !value.startsWith('/')) return null
  return `${window.location.origin.replace(/\/$/, '')}${value}`
}

const applyDocumentMeta = (meta: PageMeta): void => {
  if (typeof document === 'undefined') return

  document.title = meta.title

  let tag = document.querySelector<HTMLMetaElement>('meta[name="description"]')
  if (!tag) {
    tag = document.createElement('meta')
    tag.name = 'description'
    document.head.appendChild(tag)
  }

  tag.content = meta.description

  const imageUrl = absoluteImage(meta.imageUrl) ?? `${window.location.origin.replace(/\/$/, '')}/promo/top.jpg`
  const usingPromo = imageUrl.endsWith('/promo/top.jpg')
  const width = !usingPromo && meta.imageWidth && meta.imageHeight ? meta.imageWidth : (usingPromo ? 1170 : null)
  const height = !usingPromo && meta.imageWidth && meta.imageHeight ? meta.imageHeight : (usingPromo ? 736 : null)
  const product = meta.type === 'product' && !!meta.priceAmount

  upsertMeta('property', 'og:title', meta.title)
  upsertMeta('property', 'og:description', meta.description)
  upsertMeta('property', 'og:type', product ? 'product' : 'website')
  upsertMeta('property', 'og:locale', 'ja_JP')
  upsertMeta('property', 'og:site_name', BRAND)
  if (meta.url !== undefined) upsertMeta('property', 'og:url', meta.url)
  upsertMeta('property', 'og:image', imageUrl)
  upsertMeta('property', 'og:image:width', width == null ? null : String(width))
  upsertMeta('property', 'og:image:height', height == null ? null : String(height))
  upsertMeta('property', 'og:image:alt', meta.imageAlt || meta.title)
  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', meta.title)
  upsertMeta('name', 'twitter:description', meta.description)
  upsertMeta('name', 'twitter:image', imageUrl)
  upsertMeta('name', 'twitter:image:alt', meta.imageAlt || meta.title)
  upsertMeta('property', 'product:price:amount', product ? meta.priceAmount ?? null : null)
  upsertMeta('property', 'product:price:currency', product ? 'JPY' : null)
}

const renderMetaHtml = (meta: PageMeta): string => {
  return `<title>${escapeHtml(meta.title)}</title>\n<meta name="description" content="${escapeHtml(meta.description)}" />`
}

export {
  DESCRIPTION_MAX,
  DESCRIPTION_MIN,
  applyDocumentMeta,
  cardPageMeta,
  cardPageMetaFromItem,
  collectionPageMeta,
  defaultPageMeta,
  listingPageMeta,
  renderMetaHtml,
  setPageMeta,
  unitPageMeta,
}
export type { PageMeta }
